import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, input, viewChild } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Branch, ExternalProvider, ExternalProviderType, getErrorMessages } from '@raptor-fe/core';
import { ToastService } from '@raptor-fe/services';
import { ExternalProviderScope, ExternalProviderStore } from '../../store/external-provider.store';
import { CancelEvent, EditEvent, GridComponent, GridModule, RemoveEvent, SaveEvent } from '@progress/kendo-angular-grid';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';

@Component({
    selector: 'lib-external-provider-list',
    standalone: true,
    imports: [ReactiveFormsModule, GridModule, KENDO_DROPDOWNS],
    templateUrl: './external-provider-list.component.html',
    styles: [
        `
        :host ::ng-deep .k-grid .k-column-title {
            font-weight: bold;
        }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExternalProviderListComponent {
    readonly scope = input.required<ExternalProviderScope>();
    readonly tenantId = input.required<string>();
    readonly branchId = input<string>();
    readonly userId = input<string>();
    readonly branches = input<Branch[]>([]);
    readonly isNewEntity = input.required<boolean>();
    readonly grid = viewChild<GridComponent>('grid');

    protected readonly store = inject(ExternalProviderStore);
    private readonly alertService = inject(ToastService);
    private readonly cdr = inject(ChangeDetectorRef);

    protected readonly externalProviderTypes = Object.values(ExternalProviderType);
    protected providersFormArray = new FormArray<FormGroup>([]);
    protected selectedBranch: Branch | null = null;

    protected get providers(): FormGroup[] {
        return this.providersFormArray.controls;
    }

    protected get scopeTitle(): string {
        switch (this.scope()) {
            case 'tenant':
                return 'Tenant External Providers';
            case 'branch':
                return 'Branch External Providers';
            case 'user':
                return 'User External Providers';
        }
    }

    constructor() {
        effect(() => {
            const tenantId = this.tenantId();
            const scope = this.scope();
            const branchId = this.branchId();

            if (!tenantId) {
                return;
            }

            if (scope === 'tenant') {
                this.store.fetchByTenantId(tenantId);
            } else if (scope === 'branch' && branchId) {
                this.store.fetchByBranchId(tenantId, branchId);
            }
        });

        effect(() => {
            this.providersFormArray.clear();
            this.providersFormArray.reset();
            const providers = this.store.providers();
            if (providers) {
                providers.forEach((provider) => {
                    this.createFormGroup(provider);
                });
            }
            this.cdr.detectChanges();
        });
    }

    protected onBranchChange(branch: Branch): void {
        this.selectedBranch = branch?.id ? branch : null;
        this.cdr.detectChanges();
        if (branch?.id) {
            this.store.fetchByUserBranch(this.tenantId(), branch.id, this.userId() as string);
        } else {
            this.store.clear();
        }
    }

    protected addProvider(): void {
        const newProvider: ExternalProvider = {
            id: null as unknown as string,
            tenantId: this.tenantId(),
            branchId: this.getActiveBranchId(),
            userId: this.userId(),
            type: '' as ExternalProviderType,
            apiId: '',
            apiKey: '',
        };
        this.grid()?.editRow(this.providersFormArray.length, this.createFormGroup(newProvider));
    }

    protected editHandler(event: EditEvent): void {
        event.sender.editRow(event.rowIndex, this.providersFormArray.at(event.rowIndex));
    }

    protected cancelHandler(event: CancelEvent): void {
        const control = this.providersFormArray.at(event.rowIndex);
        if (!control.value.id) {
            this.providersFormArray.removeAt(event.rowIndex);
        } else {
            const existing = this.store.providers().find((p) => p.id === control.value.id);
            if (existing) {
                control.patchValue({
                    type: existing.type,
                    apiId: existing.apiId,
                    apiKey: existing.apiKey,
                });
                control.markAsPristine();
            }
        }
        event.sender.closeRow(event.rowIndex);
    }

    protected async saveHandler(event: SaveEvent): Promise<void> {
        const formValue = event.formGroup.value;
        const control = this.providersFormArray.at(event.rowIndex);
        const isNew = !control.value.id;

        control.patchValue({
            type: formValue.type,
            apiId: formValue.apiId,
            apiKey: formValue.apiKey,
        });

        control.markAllAsDirty();
        if (control.valid) {
            await this.store.upsert(
                this.tenantId(),
                {
                    id: control.value.id,
                    tenantId: this.tenantId(),
                    branchId: this.getActiveBranchId(),
                    userId: this.userId(),
                    type: formValue.type,
                    apiId: formValue.apiId,
                    apiKey: formValue.apiKey,
                },
                isNew,
                this.scope(),
                this.getActiveBranchId() ?? undefined,
                this.userId() ?? undefined,
            );
            event.sender.closeRow(event.rowIndex);
        }
    }

    protected async removeHandler(event: RemoveEvent): Promise<void> {
        const control = this.providersFormArray.at(event.rowIndex);
        const id = control.value.id;

        if (id) {
            const result = await this.alertService.showYesOrNoQuestion(
                'Delete External Provider',
                'Are you sure you want to delete this external provider?',
            );

            if (result === true) {
                await this.store.delete(this.tenantId(), id, this.scope(), this.getActiveBranchId() ?? undefined, this.userId() ?? undefined);
            }
        }
    }

    protected async overrideHandler(parentProvider: ExternalProvider): Promise<void> {
        await this.store.override(this.tenantId(), parentProvider, this.scope(), this.getActiveBranchId() ?? undefined, this.userId() ?? undefined);
    }

    protected getParentProvider(type: ExternalProviderType): ExternalProvider | undefined {
        const parents = this.store.allParentProviders();
        const branchMatch = parents.find((p) => p.type === type && p.branchId);
        if (branchMatch) {
            return branchMatch;
        }
        return parents.find((p) => p.type === type && !p.branchId);
    }

    protected getParentScope(provider: ExternalProvider): string {
        if (provider.branchId) {
            return 'Branch';
        }
        return 'Tenant';
    }

    protected getBadgeClass(provider: ExternalProvider): string {
        if (provider.branchId) {
            return 'bg-info text-white';
        }
        return 'bg-primary text-white';
    }

    protected getErrorMessages(control?: AbstractControl): string | undefined {
        return getErrorMessages(control);
    }

    protected getInvalidFields(formGroup: FormGroup): string {
        const fieldLabels: Record<string, string> = {
            type: 'Type',
            apiId: 'API ID',
            apiKey: 'API Key',
        };

        const invalid = Object.keys(formGroup.controls)
            .filter((key) => formGroup.get(key)?.invalid && fieldLabels[key])
            .map((key) => fieldLabels[key]);

        if (invalid.length) {
            return `Invalid: ${invalid.join(', ')}`;
        }
        return '';
    }

    private getActiveBranchId(): string | null {
        if (this.scope() === 'user') {
            return this.selectedBranch?.id ?? null;
        }
        return this.branchId() ?? null;
    }

    private createFormGroup(item: ExternalProvider): FormGroup {
        const group = new FormGroup({
            id: new FormControl(item.id ?? null),
            tenantId: new FormControl(item.tenantId ?? this.tenantId()),
            branchId: new FormControl(item.branchId ?? this.getActiveBranchId()),
            userId: new FormControl(item.userId ?? this.userId()),
            type: new FormControl(item.type ?? null, Validators.required),
            apiId: new FormControl(item.apiId ?? null),
            apiKey: new FormControl(item.apiKey ?? null),
        });

        this.providersFormArray.push(group);
        return group;
    }
}
